import { useApp } from "@/App";
import { motion } from "framer-motion";
import { Shield, Award, Users, Clock } from "lucide-react";

const stats = [
  { icon: Shield, value: "12+", label: "Let zkušeností" },
  { icon: Award, value: "10 000+", label: "Spokojených zákazníků" },
  { icon: Users, value: "15+", label: "Certifikovaných techniků" },
  { icon: Clock, value: "365", label: "Dnů v roce" },
];

export default function AboutSection() {
  const { selectedLocation } = useApp();
  const locationName = selectedLocation === "cestlice" ? "Čestlice" : "Butovice";

  return (
    <section id="about" className="py-24 md:py-32 bg-[#030303]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#39FF14] mb-4">
              O nás
            </p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-8">
              Víc než <span className="text-[#39FF14]">mytí aut</span>
            </h2>
            <div className="space-y-4 text-zinc-400 text-sm md:text-base leading-relaxed">
              <p>
                Car Spa {locationName} je profesionální studio pro kompletní péči o vaše vozidlo
                s více než <span className="text-white font-semibold">12 lety zkušeností</span> v oboru.
                Používáme výhradně prémiové produkty a nejmodernější technologie pro dosažení
                dokonalého výsledku.
              </p>
              <p>
                Náš tým certifikovaných techniků prošel intenzivním školením a má za sebou
                tisíce spokojených zákazníků. Ať už přijedete s novým vozidlem, které chcete
                ochránit keramickým povlakem, nebo s autem, které potřebuje kompletní renovaci —
                jsme tu pro vás.
              </p>
              <p>
                Každé vozidlo je pro nás unikátní projekt. Před zahájením práce provedeme
                důkladnou analýzu stavu a navrhneme optimální program péče přesně na míru
                vašemu vozu.
              </p>
            </div>

            {/* Partner logos */}
            <div className="mt-12">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-600 mb-6">
                Důvěřují nám
              </p>
              <div className="flex flex-wrap items-center gap-8">
                {["Toyota", "Drivalia", "Kuehne+Nagel", "Beko", "Škoda"].map((partner) => (
                  <div
                    key={partner}
                    className="text-sm md:text-base font-bold tracking-wider uppercase text-zinc-500 hover:text-[#39FF14] transition-colors duration-300"
                  >
                    {partner}
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              {stats.map((stat, i) => {
                const SIcon = stat.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                  >
                    <SIcon className="w-5 h-5 text-[#39FF14] mb-3" />
                    <p className="text-2xl font-black">{stat.value}</p>
                    <p className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">{stat.label}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Image grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 gap-3"
          >
            <div className="space-y-3">
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1765603712436-fd067ff74f70?q=85&w=600&auto=format&fit=crop"
                  alt="Car detailing"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1714435821841-bd9ec625b89f?q=85&w=600&auto=format&fit=crop"
                  alt="Wheel detail"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
            <div className="space-y-3 mt-8">
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1714435821865-00c4bee3b0eb?q=85&w=600&auto=format&fit=crop"
                  alt="Car emblem"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1761934657948-708146148588?q=85&w=600&auto=format&fit=crop"
                  alt="Microfiber on car"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
